import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, inject, output, viewChild, input, linkedSignal } from '@angular/core';
import {FontPickerDirective} from '@app/modules/fontPicker/fontPicker.directive';
import {PageDesignerComponent} from '@app/_components/page-designer/page-designer.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {LoaderService} from '@app/services/loader.service';
import {select, Store} from '@ngrx/store';
import {getToken, IAuthenticationState} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import { DeviceToolbarComponent } from '../device-toolbar/device-toolbar.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CdkDropListGroup, CdkDropList } from '@angular/cdk/drag-drop';
import { ColorPickerComponent } from '../color-picker/color-picker.component';
import { FontPickerDirective as FontPickerDirective_1 } from '../../modules/fontPicker/fontPicker.directive';
import { PageDesignerComponent as PageDesignerComponent_1 } from '../page-designer/page-designer.component';

@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DeviceToolbarComponent, ReactiveFormsModule, FormsModule, CdkDropListGroup, CdkDropList, ColorPickerComponent, FontPickerDirective_1, PageDesignerComponent_1]
})
export class TemplateEditorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private modalService = inject(NgbModal);
  private loaderService = inject(LoaderService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private cdr = inject(ChangeDetectorRef);

  readonly pageDesigner = viewChild<PageDesignerComponent>('pageDesigner');
  readonly viewportSizeInput = input('desktop', {alias: 'viewportSize'});
  readonly viewportSize = linkedSignal(() => this.viewportSizeInput());
  public readonly OnTemplateTypeChange = output<any>();
  public readonly OnPageSettingsChanged = output<any>();
  public readonly OnTemplateLoaded = output<any>();
  public readonly OnContentChanged = output<void>();
  readonly variables = input(undefined);
  readonly sideBarCustomStyle = input(undefined);
  readonly templateInput = input(undefined, {alias: 'template'});
  readonly template = linkedSignal(() => this.templateInput());
  readonly showTemplateSelector = input(undefined);
  readonly showBlockSelector = input(undefined);
  readonly templateType = input('');
  readonly hideTemplateSelection = input(false);
  readonly customClasses = input('');
  readonly panelWidth = input(undefined);
  readonly panelCollapsed = input(undefined);
  readonly socialImageSizes = input(undefined);
  readonly pageSettingInput = input<any>({
    max_width: this.templateType() === 'socialShare' ? '1200' : '',
    image_width: '',
    image_height: '',
    image_aspect_ratio: '',
    margin_top: '',
    block_margin: '',
    block_padding: '',
    block_background: '',
    background_color: '',
    font_color: '',
    header_font_color: '',
    link_color: '',
    button_color: '',
    button_font_color: '',
    button_border_color: '',
    button_hover_color: '',
    font_family: '',
    header_font_family: '',
    custom_css: '',
    custom_html_head_code: '',
    custom_html_body_code: '',
}, {alias: 'pageSetting'});
  readonly pageSetting = linkedSignal(() => this.pageSettingInput());
  readonly rawTemplatesTypes = input([]);
  readonly templates = input([]);
  readonly showSizeToolbar = input(false);
  readonly showLandingPageEnable = input(false);
  readonly landingPageEnabled = input(true);
  readonly landingPageEnabledChange = output<boolean>();
  public editHtml: any = {
    htmlBody: '',
    htmlEdit: false,
  };
  public defaultProperties = {
    width: '',
    height: '',
    aspect_ratio: '',
    max_width: '',
    margin_top: '0',
    block_margin: '',
    block_padding: '',
    block_background: '#ffffff',
    background_color: '#ffffff',
    font_color: '#000000',
    header_font_color: '#000000',
    link_color: '#000000',
    button_color: '#329ef7',
    button_font_color: '#ffffff',
    button_border_color: '#329ef7',
    button_hover_color: '',
    font_family: 'Arial',
    // header_font_family: 'Arial',
    header1_font_family: 'Arial',
    header2_font_family: 'Arial',
    header3_font_family: 'Arial',
    header4_font_family: 'Arial',
    header5_font_family: 'Arial',
    header6_font_family: 'Arial',
    custom_css: '',
    custom_html_head_code: '',
    custom_html_body_code: '',
  };
  public pageSizeTemplate = [
    {
      icon: 'fa-television',
      name: 'desktop',
      is_active: false,
      page_class: 'col-12 full-page'
    },
    {
      icon: 'fa-tablet',
      name: 'tablet',
      is_active: false,
      page_class: 'col-9 tablet'
    },
    {
      icon: 'fa-mobile',
      name: 'mobile',
      is_active: false,
      page_class: 'col-6 mobile'
    }
  ];
  unsubscriber = new Subject();
  selectedPredefinedSize = '';
  token = null;
  public currentMode;
  public currentToolBar: any = {imageProperties: true, bodyProperties: true, blockProperties: true, colorSettings: true, fontStyles: true, customCssSettings: true, customJavascriptSettings: true};
  public modes = {
    RAW_TEMPLATE_LISTING: 'RAW_TEMPLATE_LISTING',
    TOOL_BAR: 'TOOL_BAR',
  };
  public extraColors = [];

  private readonly codeEditor = viewChild<TemplateRef<any>>('codeEditor');
  templateLoaded = false;
  readonly isLoading = input(false);
  selectedRawType: string;

  constructor() {
    this.authenticationStore.pipe(select(getToken))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(newToken => {
        if (newToken) {
          this.token = newToken;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.templateInput && changes.templateInput.currentValue) {
      this.initHTMLTemplate();
    }
    const rawTemplatesTypes = this.rawTemplatesTypes();
    if (rawTemplatesTypes && changes.rawTemplatesTypes && changes.rawTemplatesTypes.currentValue) {
      if (rawTemplatesTypes && changes.rawTemplatesTypes.currentValue.length > 0) {
        this.selectedRawType = changes.rawTemplatesTypes.currentValue[0].id;
      }
    }
  }

  ngOnInit() {
    this.currentMode = this.modes.RAW_TEMPLATE_LISTING;
  }

  ngAfterViewInit() {
    this.initHTMLTemplate();
  }

  initHTMLTemplate() {
    const template = this.template();
    if (template && template['body'] && template['style']) {
      this.OnTemplateLoaded.emit(false);
      this.templateLoaded = false;
      this.pageDesigner().setHtml({body: template['body'], styles: template['style']}, () => {
        this.OnTemplateLoaded.emit(true);
        this.templateLoaded = true;
      });
      const t = template || {};
      let properties = t.properties;
      const templateType = this.templateType();
      if (properties) {
        while (typeof properties === 'string') {
          properties = JSON.parse(properties);
        }
        properties = {
          ...properties,
          image_width: template.width,
          image_height: template.height,
          image_aspect_ratio: template.aspect_ratio,
        };
        // Only image-sized templates (social share graphics) should pin the
        // iframe to a fixed pixel size. Landing/email pages are fluid and must
        // stay at CSS's width:100% — otherwise any leftover width/height on the
        // template record (e.g. inherited from a design-block's original size)
        // forces the canvas narrower than its container, which #froala-pages's
        // own `margin: auto` then centers, showing as big empty side margins.
        if (templateType === 'socialShare') {
          this.pageDesigner().setIframeSizes(template.width, template.height, null);
        }
        Object.entries(this.pageSetting()).forEach(([key, val]) => {
          this.setDefaultProperties(key, properties);
          this.onStylesCustomizationChange(properties[key], key);
        });
        if (properties.predefined_size_id) {
          this.selectedPredefinedSize = properties.predefined_size_id;
        }
      }
      if (!properties && templateType === 'socialShare') {
        const defaultProperties = {max_width : '1200'};
        Object.entries(this.pageSetting()).forEach(([key, val]) => {
          this.setDefaultProperties(key, defaultProperties);
          this.onStylesCustomizationChange(defaultProperties[key], key);
        });
      }
      this.cdr.detectChanges();
    } else {
      setTimeout(() => {
        this.templateLoaded = true;
        this.OnTemplateLoaded.emit(true);
        this.cdr.detectChanges();
      }, 3000);
    }
  }

  setDefaultProperties(key, properties) {
    if (!properties[key]) {
      properties[key] = this.defaultProperties[key];
    }
  }

  onListIconClick() {
    this.currentMode = this.modes.RAW_TEMPLATE_LISTING;
  }

  onBrushIconClick() {
    this.currentMode = this.modes.TOOL_BAR;
  }

  onRawTemplateTypeChange(event) {
    const {options} = event.target;
    const {selectedIndex} = options;
    this.selectedRawType = options[selectedIndex].getAttribute('value');

    this.OnTemplateTypeChange.emit(this.selectedRawType);
  }

  async buildTemplateForSave() {
    const htmlParts = await this.pageDesigner().getHtmlParts();
    return {body: htmlParts.body, style: htmlParts.styles};
  }

  addBlock(item) {
    if (this.showTemplateSelector()) {
      this.template.set({...item});
      this.initHTMLTemplate();
    } else {
      const val = item.body.replaceAll('\\', '');
      this.pageDesigner().appendBlock(val, item.template_section_type.name);
    }
  }

  getVariablesForPageDesigner() {
    const vars = this.variables();
    if (vars) {
      const result = {};
      Object.entries(vars).forEach(([key, value]) => {
        if (value.hasOwnProperty('group') && value['group']) {
          if (!result[value['group']]) {
            result[value['group']] = [];
          }
          result[value['group']].push({
            name: value['title'],
            key: value['alias'] ? `#${value['alias']}#` : value['key'],
            valueType: value['valueType'],
          });
        } else {
          if (!result['generic']) {
            result['generic'] = [];
          }
          result['generic'].push({
            name: value['title'],
            key: value['key'],
            valueType: value['valueType'],
          });
        }
      });
      return result;
    }
  }

  getAspectRatio(width: number | string, height: number | string): string {
    const w = Number(width);
    const h = Number(height);

    if (!w || !h) {
      return '';
    }

    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(w, h);

    return `${w / divisor}:${h / divisor}`;
  }

  onStylesCustomizationChange(value, field) {
    let colorType = false;
    switch (field) {
      case 'image_width':
        this.pageDesigner().setIframeSizes(value || null, null, null);
        break;
      case 'image_height':
        this.pageDesigner().setIframeSizes(null, value || null, null);
        this.pageDesigner().setEditorHeight(Number(value) || null);
        break;
      case 'margin_top':
        this.pageDesigner().changeStyle('body', 'paddingTop', (value) ? value + 'px' : '');
        break;
      case 'max_width':
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'marginRight', 'auto');
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'marginLeft', 'auto');
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'maxWidth', value ? value + 'px' : '');
        break;
      case 'block_margin':
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'margin', (value) ? value + 'px' : '');
        break;
      case 'block_padding':
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'padding', value ? value + 'px' : '', true);
        break;
      case 'block_background':
        this.pageDesigner().changeStyle('section.fdb-block, header, footer', 'backgroundColor', value);
        colorType = true;
        break;
      case 'background_color':
        this.pageDesigner().changeStyle('body', 'backgroundColor', value);
        colorType = true;
        break;
      case 'font_family':
        this.pageDesigner().changeStyle('body', 'fontFamily', value);
        this.pageDesigner().changeStyle('p', 'fontFamily', value);
        this.pageDesigner().changeStyle('li', 'fontFamily', value);
        break;
      case 'font_color':
        this.pageDesigner().changeStyle('body', 'color', value);
        this.pageDesigner().changeStyle('p', 'color', value);
        colorType = true;
        break;
      case 'header_font_color':
        this.pageDesigner().changeStyle('h1, h2, h3, h4, h5, h6', 'color', value);
        colorType = true;
        break;
      // case 'header_font_family':
      //   this.pageDesigner.changeStyle('h1, h2, h3, h4, h5, h6', 'fontFamily', value);
      //   break;
      case 'header1_font_family':
        this.pageDesigner().changeStyle('h1', 'fontFamily', value);
        break;
      case 'header2_font_family':
        this.pageDesigner().changeStyle('h2', 'fontFamily', value);
        break;
      case 'header3_font_family':
        this.pageDesigner().changeStyle('h3', 'fontFamily', value);
        break;
      case 'header4_font_family':
        this.pageDesigner().changeStyle('h4', 'fontFamily', value);
        break;
      case 'header5_font_family':
        this.pageDesigner().changeStyle('h5', 'fontFamily', value);
        break;
      case 'header6_font_family':
        this.pageDesigner().changeStyle('h6', 'fontFamily', value);
        break;
      case 'link_color':
        this.pageDesigner().changeStyle('a', 'color', value);
        colorType = true;
        break;
      case 'button_color':
        this.pageDesigner().changeStyle('button', 'backgroundColor', value);
        this.pageDesigner().changeStyle('a.btn', 'backgroundColor', value);
        this.pageDesigner().changeStyle('a.btn.btn-secondary', 'backgroundColor', value);
        colorType = true;

        // Change border
        // this.pageDesigner.changeStyle('button', 'border-color', value);
        // this.pageDesigner.changeStyle('a.btn', 'border-color', value);
        // this.pageDesigner.changeStyle('a.btn.btn-secondary', 'border-color', value);
        break;
      case 'button_font_color':
        this.pageDesigner().changeStyle('button', 'color', value);
        this.pageDesigner().changeStyle('a.btn', 'color', value);
        this.pageDesigner().changeStyle('a.btn.btn-secondary', 'color', value);
        colorType = true;
        break;
      case 'button_border_color':
        this.pageDesigner().changeStyle('button', 'border-color', value);
        this.pageDesigner().changeStyle('a.btn', 'border-color', value);
        this.pageDesigner().changeStyle('a.btn.btn-secondary', 'border-color', value);
        colorType = true;
        break;
      case 'button_hover_color':
        this.pageDesigner().changeStyle('button:hover', 'backgroundColor', value, true);
        this.pageDesigner().changeStyle('a.btn:hover', 'backgroundColor', value, true);
        this.pageDesigner().changeStyle('a.btn.btn-secondary:hover', 'backgroundColor', value, true);
        colorType = true;

        // this.pageDesigner.changeStyle('button:hover', 'border-color', value, true);
        // this.pageDesigner.changeStyle('a.btn:hover', 'border-color', value, true);
        // this.pageDesigner.changeStyle('a.btn.btn-secondary:hover', 'border-color', value, true);
        break;
      case 'button_font_family':
        this.pageDesigner().changeStyle('button', 'fontFamily', value);
        break;
      case 'custom_html_head_code':
        //this.pageDesigner.customCSSStyles(value);
        break;
      case 'custom_html_body_code':
        //this.pageDesigner.customCSSStyles(value);
        break;
      case 'custom_css':
        this.pageDesigner().customCSSStyles(value);
        break;
    }
    if (colorType) {
      this.extraColors = [...new Set([value, ...this.extraColors])].filter(x => (x && x.includes('#'))).slice(0, 14);
    }
    this.pageSetting.set({
      ...this.pageSetting(),
      [field]: value
    });

    if (field === 'image_width' || field === 'image_height') {
      this.pageSetting.set({
        ...this.pageSetting(),
        image_aspect_ratio: this.getAspectRatio(this.pageSetting().image_width, this.pageSetting().image_height),
      });
    }

    this.OnPageSettingsChanged.emit(this.pageSetting());
  }

  onWidthHeightChange(value: number, field: 'image_width' | 'image_height') {
    this.selectedPredefinedSize = '';
    this.onStylesCustomizationChange('', 'predefined_size_id');
    this.onStylesCustomizationChange(value, field);
  }

  onPredefinedSizeChange(selectedId: string) {
    if (!selectedId) {
      this.onStylesCustomizationChange('', 'predefined_size_id');
      return;
    }

    const selectedSize = this.socialImageSizes().find(s => s.id === Number(selectedId));
    if (selectedSize) {
      this.onStylesCustomizationChange(selectedId, 'predefined_size_id');
      this.onStylesCustomizationChange(`${selectedSize.width}`, 'image_width');
      this.onStylesCustomizationChange(`${selectedSize.height}`, 'image_height');
    }
  }

  applyHtml() {
    this.templateLoaded = false;
    this.OnTemplateLoaded.emit(false);
    this.pageDesigner().setHtml({body: this.editHtml.htmlBody, styles: ''}, () => {
      this.OnTemplateLoaded.emit(true);
      this.templateLoaded = true;
    });
    Object.entries(this.pageSetting()).forEach(([key, val]) => {
      const pageSetting = this.pageSetting();
      if (pageSetting && pageSetting[key]) {
        this.onStylesCustomizationChange(pageSetting[key], key);
      }
    });
    this.onEditHtml();
  }

  onPageSizeSelect(item) {
    this.viewportSize.set(item);
  }

  // Prevent lagging in textarea due to 2 way binding
  updateHtmlBody(event) {
    this.editHtml.htmlBody = event.target.value;
  }

  onEditHtml() {
    const pageDesigner = this.pageDesigner();
    if (pageDesigner) {
      if (!this.editHtml.htmlEdit) {
        this.loaderService.show();
        const modalRef = this.modalService.open(this.codeEditor(), {
          size: 'xl'
        });
        modalRef.result.then(result => {
          this.applyHtml();
        }, reason => {
          this.onEditHtml();
        });
        for (const pageSize of this.pageSizeTemplate) {
          pageSize.is_active = false;
        }
        pageDesigner.getHtmlParts().then(parts => {
          this.pageDesigner().hideToolbars();
          this.loaderService.hide();
          this.editHtml.htmlEdit = true;
          this.editHtml.htmlBody = parts.body;
        });
      } else {
        this.editHtml.htmlEdit = false;
        this.editHtml.htmlBody = '';
      }
    }
  }

  destroy() {
    this.pageDesigner().destroy();
  }

  disableEditor() {
    this.pageDesigner().disableEditor();
  }

  enableEditor() {
    this.pageDesigner().enableEditor();
  }

  ngOnDestroy() {
    this.pageDesigner().destroy();
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
